"use client";

import { useLayoutEffect, useRef } from "react";
import { usePathname } from "next/navigation";

/**
 * New internal link navigations start at the top. AdSense vignettes sometimes
 * restore the previous document's scroll position AFTER Next.js has committed
 * its new route. Correct that delayed restore without disrupting real user
 * scrolling or browser back/forward restoration.
 */
export default function RouteScrollReset() {
  const pathname = usePathname();
  const previousPath = useRef<string | null>(null);
  const nextForward = useRef(false);
  const lastClickedScrollY = useRef(0);

  useLayoutEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        event.altKey
      ) return;

      const link = event.target instanceof Element
        ? event.target.closest<HTMLAnchorElement>("a[href]")
        : null;
      if (!link || link.hasAttribute("download") || (link.target && link.target !== "_self")) return;

      let target: URL;
      try {
        target = new URL(link.href, window.location.href);
      } catch {
        return;
      }

      const current = new URL(window.location.href);
      if (
        target.origin === current.origin &&
        (target.pathname !== current.pathname || target.search !== current.search)
      ) {
        nextForward.current = true;
        lastClickedScrollY.current = window.scrollY;
      }
    };
    const onPopState = () => {
      nextForward.current = false;
    };

    document.addEventListener("click", onClick, true);
    window.addEventListener("popstate", onPopState);
    return () => {
      document.removeEventListener("click", onClick, true);
      window.removeEventListener("popstate", onPopState);
    };
  }, []);

  useLayoutEffect(() => {
    if (previousPath.current === null) {
      previousPath.current = pathname;
      return;
    }
    if (previousPath.current === pathname) return;
    previousPath.current = pathname;

    const forward = nextForward.current;
    nextForward.current = false;
    if (!forward) return;

    // Do not compete with the fullscreen overlay while it is open. Google's
    // own code restores the old scroll position as the overlay closes.
    // This effect normally runs once the new route has been committed.
    const originalY = lastClickedScrollY.current;
    let userInteracted = false;
    let corrections = 0;
    let finished = false;
    let raf = 0;
    let timer = 0;
    const startedAt = performance.now();
    const settleWindowMs = 2000;
    const minJumpPx = 80;

    const onUserInput = () => {
      userInteracted = true;
      finish();
    };
    const finish = () => {
      if (finished) return;
      finished = true;
      window.cancelAnimationFrame(raf);
      window.clearTimeout(timer);
      window.removeEventListener("wheel", onUserInput);
      window.removeEventListener("touchstart", onUserInput);
      window.removeEventListener("pointerdown", onUserInput);
      window.removeEventListener("keydown", onUserInput);
      window.removeEventListener("scroll", onScroll);
    };
    const correct = () => {
      if (finished || userInteracted || location.hash === "#google_vignette") return;
      // Limit corrections; never trap visitors at the top of a page.
      if (corrections >= 3 || performance.now() - startedAt > settleWindowMs) {
        finish();
        return;
      }
      const currentY = window.scrollY;
      // Ignore small layout shifts. For vignette restores, the position is
      // typically equal (or close) to the position on the previous route.
      const resemblesRestore = Math.abs(currentY - originalY) < 140;
      if (currentY >= minJumpPx && (resemblesRestore || corrections === 0)) {
        corrections += 1;
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      }
    };
    const onScroll = () => {
      if (finished || userInteracted || location.hash === "#google_vignette") return;
      window.cancelAnimationFrame(raf);
      raf = window.requestAnimationFrame(correct);
    };

    window.addEventListener("wheel", onUserInput, { passive: true });
    window.addEventListener("touchstart", onUserInput, { passive: true });
    window.addEventListener("pointerdown", onUserInput, { passive: true });
    window.addEventListener("keydown", onUserInput);
    window.addEventListener("scroll", onScroll, { passive: true });

    if (location.hash !== "#google_vignette") {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }
    timer = window.setTimeout(finish, settleWindowMs);
    return finish;
  }, [pathname]);

  return null;
}
